import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import Style from './Governance.module.css';
import DataControls from './DataControls';
import PolicyVersionBanner from './PolicyVersionBanner';
import GovernanceLifecycleModals from './GovernanceLifecycleModals';
import backButton from '../../assets/backButton.svg';
import LoaderWrapper from '../common/LoaderWrapper/LoaderWrapper';
import useGovernanceLifecycle from './useGovernanceLifecycle';
import { draftMetaOf } from './GovernanceShared';
import {
  getGovernancePolicy,
  getGovernanceDraft,
  getGovernanceDefaultRules,
  createGovernanceDraft,
  approveGovernancePolicy,
  rejectGovernancePolicy,
  publishGovernancePolicy,
  addGovernanceRule,
  updateGovernanceRule,
  deleteGovernanceRule,
} from '../../store/entities/governance';

// Data Controls editor — one of the control-type editors GovernanceEditor
// dispatches to (?type=DATA, the default). It owns the DATA rule staging (local
// rules + placeholder default templates + published→draft ruleId remap); the
// version lifecycle (banner flow + modals) is shared with AppControlsEditor via
// useGovernanceLifecycle + GovernanceLifecycleModals.
const MODAL_COPY = {
  rejectPlaceholder: 'e.g. Missing required detection categories…',
  publishDescription:
    'Publishing makes this draft the live policy enforced by the extension. The current published version will be archived. Continue?',
};

const DataControlsEditor = (props) => {
  const { draft, policy, draftLoading, publishing } = props;
  // Bug 4: always use the URL param as the authoritative customer ID — avoids a
  // second API call when Redux populates `selected` after mount.
  const customerId = props.match?.params?.id;

  // ── LOCAL RULE STAGING ───────────────────────────────────────────────────────
  // Rules are edited locally and only sent to the API when "Save Draft" is clicked.
  const [localRules, setLocalRules] = useState([]);
  const [deletedRuleIds, setDeletedRuleIds] = useState([]); // real ruleIds to DELETE on save
  const [tempCounter, setTempCounter] = useState(-1);       // negative IDs for not-yet-saved rules

  // ── draft meta ──
  const { draftExists, draftVersion, policyVersionId, draftStatus, isEditable } = draftMetaOf(draft);

  // A customer with NO published policy and NO draft is a brand-new policy: the
  // editor shows the default rule templates, but there's nothing "dirty" yet, so
  // Save Draft would otherwise be hidden and the defaults could never be
  // persisted. Allow the initial save so "Create Policy" can seed the first draft.
  const hasPublishedPolicy = !!(policy && policy.version);
  const allowInitialSave = !draftExists && !hasPublishedPolicy;

  const hasLocalChanges =
    localRules.some((r) => r._tempId !== undefined || r._dirty) ||
    deletedRuleIds.length > 0;

  // ── draft creation (lazy) ───────────────────────────────────────────────────
  // Creates/resumes the draft and returns { policyVersionId, rules }.
  // Bug 1: when creating a fresh draft from published rules, the proc assigns NEW
  // ruleIds to each cloned rule. We reload the draft here so callers can build a
  // publishedRuleId → draftRuleId remap before issuing PUTs/DELETEs.
  const ensureDraft = async () => {
    if (draftExists) return { policyVersionId, rules: draft.rules || [], freshlyCreated: false };
    await props.createGovernanceDraft(customerId);
    const res = await props.getGovernanceDraft(customerId);
    const d = res?.data?.[0] ?? null;
    if (!d || !d.version) return null;
    return {
      policyVersionId: d.version.PolicyVersionId ?? d.version.policyVersionId,
      rules: d.rules || [],
      freshlyCreated: true,
    };
  };

  // ── batch save ──────────────────────────────────────────────────────────────
  const flushPendingChanges = async () => {
    const hadDraft = draftExists;

    const hasChanges =
      localRules.some((r) => r._tempId !== undefined || r._dirty) ||
      deletedRuleIds.length > 0;

    // Even with no staged changes, we still need to ensure a draft exists (e.g.
    // Submit for Approval on an untouched but draft-less editor).
    const ens = await ensureDraft();
    if (!ens?.policyVersionId) return null;
    const vid = ens.policyVersionId;

    if (!hasChanges) return vid;

    // Bug 1: if we just created a fresh draft by cloning the published version,
    // the clone assigns NEW ruleIds. Build a map so _dirty rules and
    // deletedRuleIds can be remapped to the draft's actual ruleIds.
    //
    // Two independent rules can legally share the same (scanRuleId, sourceCode)
    // — e.g. two CUSTOM rules on one scan rule with different actions — so keying
    // on that pair alone collides and remaps both to the first draft row. Match on
    // the full cloned identity (scanRuleId + sourceCode + description + action +
    // category) AND consume each draft row once, so even exact duplicates get a
    // distinct 1:1 mapping instead of one overwriting the other.
    let publishedToDraftId = new Map();
    if (!hadDraft && ens.freshlyCreated && policy && Array.isArray(policy.rules)) {
      const draftRows = Array.isArray(ens.rules) ? [...ens.rules] : [];
      const usedDraftIdx = new Set();
      const sameClonedRule = (pub, dr) =>
        dr.scanRuleId === pub.scanRuleId &&
        dr.sourceCode === pub.sourceCode &&
        (dr.ruleDescription ?? '') === (pub.ruleDescription ?? '') &&
        dr.actionCode === pub.actionCode &&
        (dr.categoryCode ?? null) === (pub.categoryCode ?? null);
      policy.rules.forEach((pubRule) => {
        const idx = draftRows.findIndex((dr, i) => !usedDraftIdx.has(i) && sameClonedRule(pubRule, dr));
        if (idx !== -1) {
          usedDraftIdx.add(idx);
          publishedToDraftId.set(pubRule.ruleId, draftRows[idx].ruleId);
        }
      });
    }

    const remapId = (id) => publishedToDraftId.get(id) ?? id;

    // The api middleware resolves the response body (always defined, even '' for
    // a 204) on success and `undefined` on failure — so `=== undefined` reliably
    // flags a failed call. Collect every result and treat any undefined as a
    // batch failure, so the caller surfaces an error and does NOT proceed to
    // approve / navigate on a partial save.
    const results = [];

    // POST new rules (negative _tempId = not yet in DB; skip display-only placeholders)
    const newRules = localRules.filter((r) => r._tempId !== undefined && !r._placeholder);
    results.push(...await Promise.all(
      newRules.map(({ _tempId, ...payload }) =>
        props.addGovernanceRule(customerId, { ...payload, policyVersionId: vid })
      )
    ));

    // PUT modified existing rules (remap ruleId if the draft was just created)
    const dirtyRules = localRules.filter((r) => r.ruleId && r._dirty);
    results.push(...await Promise.all(
      dirtyRules.map(({ _dirty, ...payload }) => {
        const actualId = remapId(payload.ruleId);
        return props.updateGovernanceRule(customerId, actualId, { ...payload, ruleId: actualId, policyVersionId: vid });
      })
    ));

    // DELETE removed rules (remap ruleId if needed)
    const actualDeleteIds = deletedRuleIds.map(remapId);
    results.push(...await Promise.all(
      actualDeleteIds.map((ruleId) =>
        props.deleteGovernanceRule(customerId, ruleId, vid)
      )
    ));

    if (results.some((r) => r === undefined)) return null;

    return vid;
  };

  // ── shared lifecycle (banner flow + modals) ─────────────────────────────────
  const lc = useGovernanceLifecycle({
    customerId, controlType: 'DATA', history: props.history,
    policy, publishing, policyVersionId,
    flush: flushPendingChanges, dirty: hasLocalChanges, allowInitialSave, saveErrorNoun: 'rules',
    getGovernanceDraft: props.getGovernanceDraft,
    getGovernancePolicy: props.getGovernancePolicy,
    approveGovernancePolicy: props.approveGovernancePolicy,
    rejectGovernancePolicy: props.rejectGovernancePolicy,
    publishGovernancePolicy: props.publishGovernancePolicy,
  });

  // Seed localRules once the initial data loads (and after each in-place refresh).
  //
  // Three cases:
  //   1. Open draft exists          → use draft.rules (real ruleIds, ready to edit)
  //   2. Published rules exist      → use policy.rules; draft created lazily on Save
  //   3. Brand-new policy (no draft, no published rules) → fetch the default rule
  //      templates and display them as read-only placeholders; the draft is
  //      created only when Save Draft is clicked (Case 2 stays lazy too, to avoid
  //      spurious DB writes when the admin just opens the editor and cancels).
  useEffect(() => {
    if (!lc.ready) return;
    if (!customerId) { setLocalRules([]); setDeletedRuleIds([]); return; }

    const openDraft      = (draft && draft.version) ? draft : null;
    const publishedRules = policy && Array.isArray(policy.rules) ? policy.rules : [];

    // Case 1: an open draft already exists → edit its rows directly.
    if (openDraft) {
      setLocalRules(Array.isArray(openDraft.rules) ? openDraft.rules.map((r) => ({ ...r })) : []);
      setDeletedRuleIds([]);
      return;
    }

    // Case 2: a published policy exists but no draft is open → seed from published
    // rows and stay lazy (draft created only on Save; ids remapped there).
    if (publishedRules.length > 0) {
      setLocalRules(publishedRules.map((r) => ({ ...r })));
      setDeletedRuleIds([]);
      return;
    }

    // Case 3: brand-new policy (no draft, no published rules) — fetch the default
    // rule templates and display them as read-only placeholders. No draft created.
    let cancelled = false;
    (async () => {
      const res = await props.getGovernanceDefaultRules(customerId);
      if (cancelled) return;
      const rules = Array.isArray(res?.data) ? res.data : [];
      setLocalRules(rules.map((r) => ({ ...r, _placeholder: true })));
      setDeletedRuleIds([]);
    })();
    return () => { cancelled = true; };
  }, [lc.ready, lc.reseedKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── local rule handlers (NO API calls) ─────────────────────────────────────
  const handleAddRule = (payload) => {
    const id = tempCounter;
    setTempCounter((c) => c - 1);
    setLocalRules((prev) => [...prev, { ...payload, _tempId: id }]);
  };

  const handleEditRule = (ruleId, payload) => {
    setLocalRules((prev) =>
      prev.map((r) => (r.ruleId === ruleId ? { ...r, ...payload, _dirty: true } : r))
    );
  };

  // Bug 2: editing a newly-added (temp) rule must match by _tempId, not ruleId.
  const handleEditTempRule = (tempId, payload) => {
    setLocalRules((prev) =>
      prev.map((r) => (r._tempId === tempId ? { ...r, ...payload } : r))
    );
  };

  const handleDeleteRule = (rule) => {
    if (rule.ruleId) setDeletedRuleIds((prev) => [...prev, rule.ruleId]);
    setLocalRules((prev) =>
      prev.filter((r) =>
        rule.ruleId ? r.ruleId !== rule.ruleId : r._tempId !== rule._tempId
      )
    );
  };

  // Bug 2: unified handler — DataControls passes the full existingRule object (or
  // null for brand-new additions) so we can route by _tempId vs ruleId.
  const handleSaveRule = (payload, existingRule) => {
    if (!existingRule) {
      handleAddRule(payload);
    } else if (existingRule._tempId !== undefined) {
      handleEditTempRule(existingRule._tempId, payload);
    } else {
      handleEditRule(existingRule.ruleId, payload);
    }
  };

  // ── header ──────────────────────────────────────────────────────────────
  // Published version is shown as a muted subtitle here (NOT a green badge) —
  // on the editor the green lives in the stepper, not the title.
  return (
    <div className={Style.page}>
      {/* ── Fixed header ── */}
      <div className={Style.gvHeader}>
        {/* Return link mirrors the incidentDetails "Return to Overview" affordance. */}
        <div className={Style.returnLink} onClick={lc.backToLanding}>
          <img width="22px" src={backButton} alt="" />
          <span>Return to Data Controls</span>
        </div>
        <h1 className={Style.pageTitle}>Edit Data Controls Policy</h1>
        <p className={Style.pageSubtitle}>
          Rules are staged locally. Use the version banner below to save the draft,
          submit it for approval, or publish.
        </p>
        <p className={Style.publishedSubtitle}>{lc.publishedText()}</p>
      </div>

      {/* ── Scrollable policy-definition area ── */}
      <div className={Style.gvScrollBody}>
        {/* Version-lifecycle banner: stepper + status + the single transforming
            action (Save Draft → Submit for Approval → Publish), plus Reject +
            Cancel. */}
        <PolicyVersionBanner
          statusCode={draftStatus}
          versionNumber={draftVersion ? (draftVersion.VersionNumber ?? draftVersion.versionNumber) : null}
          draftExists={draftExists}
          dirty={hasLocalChanges}
          allowInitialSave={allowInitialSave}
          onSaveDraft={lc.handleSaveDraft}
          onSubmitForApproval={() => lc.setPopup('approve')}
          onReject={() => lc.setPopup('reject')}
          onPublish={() => lc.setPopup('publish')}
          onCancel={lc.handleCancelClick}
          saving={lc.savingDraft}
          publishing={lc.publishing}
          disabled={lc.isBusy}
        />
        <LoaderWrapper loading={!lc.ready || draftLoading} waitForData={false} styles={{ minHeight: '40px' }} isFlex={true}>
          <DataControls
            editable={isEditable}
            rules={localRules}
            onSaveRule={handleSaveRule}
            onDeleteRule={handleDeleteRule}
          />
        </LoaderWrapper>
      </div>

      <GovernanceLifecycleModals
        popup={lc.popup} setPopup={lc.setPopup}
        rejectReason={lc.rejectReason} setRejectReason={lc.setRejectReason}
        errorPopup={lc.errorPopup} setErrorPopup={lc.setErrorPopup}
        isBusy={lc.isBusy} publishing={lc.publishing}
        onCancelConfirmed={lc.handleCancelConfirmed}
        onApproveConfirmed={lc.handleApproveConfirmed}
        onRejectConfirmed={lc.handleRejectConfirmed}
        onPublishConfirmed={lc.handlePublishConfirmed}
        rejectPlaceholder={MODAL_COPY.rejectPlaceholder}
        publishDescription={MODAL_COPY.publishDescription}
      />
    </div>
  );
};

const mapStateToProps = (state) => ({
  draft: state.entities.governance.draft,
  policy: state.entities.governance.policy,
  draftLoading: state.entities.governance.draftLoading,
  publishing: state.entities.governance.publishing,
});

const mapDispatchToProps = (dispatch) => ({
  getGovernancePolicy: (customerId) => Promise.resolve(dispatch(getGovernancePolicy(customerId))),
  getGovernanceDraft: (customerId) => Promise.resolve(dispatch(getGovernanceDraft(customerId))),
  getGovernanceDefaultRules: (customerId) => Promise.resolve(dispatch(getGovernanceDefaultRules(customerId))),
  createGovernanceDraft: (customerId) => Promise.resolve(dispatch(createGovernanceDraft(customerId))),
  addGovernanceRule: (customerId, body) => Promise.resolve(dispatch(addGovernanceRule(customerId, body))),
  updateGovernanceRule: (customerId, ruleId, body) => Promise.resolve(dispatch(updateGovernanceRule(customerId, ruleId, body))),
  deleteGovernanceRule: (customerId, ruleId, policyVersionId) => Promise.resolve(dispatch(deleteGovernanceRule(customerId, ruleId, policyVersionId))),
  approveGovernancePolicy: (customerId, versionId) => Promise.resolve(dispatch(approveGovernancePolicy(customerId, versionId))),
  rejectGovernancePolicy: (customerId, versionId, reason) => Promise.resolve(dispatch(rejectGovernancePolicy(customerId, versionId, reason))),
  publishGovernancePolicy: (customerId, versionId) => Promise.resolve(dispatch(publishGovernancePolicy(customerId, versionId))),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(DataControlsEditor));
